import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Subject, takeUntil, of, from, forkJoin } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@app/shared/components/data-table/data-table.component';
import { StockDeliveryService } from '../../../stock-delivery.service';
import { OutboundDeliveryItem, TrackingPolicy } from '../../../stock-delivery.config';
import { BASE_PATH } from '@app/api';
import { environment } from '@environments/environment';
import { ResultRecordControllerService } from '@app/api';
import { OutboundDeliveryOrderService } from '@app/api/stock-service';
import { OutboundDeliveryOrderPickingDTO } from '@app/api/stock-service/model/outboundDeliveryOrderPickingDTO';
import { PickingItemDTO } from '@app/api/stock-service/model/pickingItemDTO';
import { SerialProfileManagementService } from '@app/api/stock-service/api/serialProfileManagement.service';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { BarcodeResolverService } from '@app/api/goods-service/api/barcodeResolver.service';
import { BarcodeResolveResultVO } from '@app/api/goods-service/model/barcodeResolveResultVO';
import { Gs1ParseResultVO } from '@app/api/goods-service/model/gs1ParseResultVO';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { parseScannedBarcode } from '@app/shared/utils/parse-scanned-barcode.util';
import {
  FefoOverrideDialogComponent,
  FefoOverrideDialogData,
  FefoOverrideDialogResult,
} from '../../../../shared/fefo-override-dialog/fefo-override-dialog.component';

export interface PickLine {
  id: string;
  skuCode: string;
  skuName: string;
  spuCode: string;
  lot?: string;
  batchId?: string;
  fefoSuggestedBatchId?: string;
  overrideReason?: string;
  serial?: string;
  quantity: number;
  plannedQty?: number;
  pickedQty?: number;
  waitOutQty?: number;
  policy?: TrackingPolicy;
}

export interface LotOption {
  label: string;
  value: string;
  isAddOption?: boolean;
}


export interface PickLineGroup {
  skuCode: string;
  skuName: string;
  spuCode: string;
  lines: PickLine[];
  totalQuantity: number;
  isExpanded: boolean;
  plannedQty?: number;
  pickedQty?: number;
  waitOutQty?: number;
}

@Component({
  selector: 'app-pick-goods',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    AppButtonComponent,
    InputTextModule,
    InputNumberModule,
    TableModule,
    SelectModule,
    RadioButtonModule,
    DataTableComponent,
    FefoOverrideDialogComponent,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    ResultRecordControllerService,
    StockDeliveryService,
  ],
  templateUrl: './pick-goods.component.html',
  styleUrl: './pick-goods.component.css',
})
export class PickGoodsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly stockDeliveryService = inject(StockDeliveryService);
  private readonly outboundDeliveryOrderService = inject(OutboundDeliveryOrderService);
  private readonly serialProfileManagementService = inject(SerialProfileManagementService);
  private readonly batchManagementService = inject(BatchManagementService);
  private readonly barcodeResolverService = inject(BarcodeResolverService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Route params
  orderId: string | null = null;
  warehouseId: number | null = null;

  // Form
  trackingForm!: FormGroup;
  trackingLinesFormArray!: FormArray;
  addTrackingLineForm!: FormGroup;

  // Simple properties
  lotFromBarcode: string | undefined = undefined;

  // Form inputs for manual add
  selectedProduct: OutboundDeliveryItem | null = null;
  previousProductSkuCode: string | null = null;
  lotInput = '';
  serialInput = '';
  quantityInput = 1;

  editingTrackingLineId: string | null = null;

  fefoOverrideVisible = false;
  fefoOverrideData: FefoOverrideDialogData | null = null;
  private pendingFefoLines: PickLine[] = [];

  // Barcode scanning
  formBarcodeInput = '';
  scanningBarcode = false;

  // Lot management
  lotOptions: string[] = [];
  lotOptionsMap: Map<string, string[]> = new Map();
  lotOptionMap: Map<string, LotOption[]> = new Map();

  // Tracking lines
  pickLines: PickLine[] = [];
  pickLineGroups: PickLineGroup[] = [];
  expandedSkuGroups = new Set<string>();
  tableDataSource: any[] = [];

  // Data
  items: OutboundDeliveryItem[] = [];
  loading = false;
  private skipCanDeactivate = false;

  // Selected ODO item for auto-fill
  selectedODOItem: any = null;

  // Table columns for ODO list (left column)
  odoListColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      width: 100,
      type: 'text',
      align: 'left',
    },
    {
      label: 'outbound_management.fields.planned_quantity',
      prop: 'plannedQty',
      width: 30,
      type: 'number',
      align: 'right',
    },
    {
      label: 'outbound_management.fields.remaining_quantity',
      prop: 'remainingQty',
      width: 30,
      type: 'number',
      align: 'right',
    },
  ];

  // Data source for ODO list (left column)
  odoListDataSource: any[] = [];

  // Table columns
  tableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      width: 180,
      type: 'custom',
      align: 'left',
    },
    {
      label: 'outbound_management.fields.planned_quantity',
      prop: 'plannedQty',
      width: 100,
      type: 'number',
      align: 'right',
    },
    {
      label: 'outbound_management.fields.already_out_quantity',
      prop: 'pickedQty',
      width: 100,
      type: 'number',
      align: 'right',
    },
    {
      label: 'inbound.fields.quantity',
      prop: 'quantity',
      width: 90,
      type: 'custom',
      align: 'right',
      min: 0,
    },
    {
      label: 'common.actions.action',
      prop: 'action',
      width: 80,
      type: 'custom',
      align: 'center',
    },
  ];

  ngOnInit(): void {
    this.orderId = this.route.snapshot.paramMap.get('id');
    
    if (!this.orderId) {
      this.showErrorMessage('inbound.receive.invalid_warrant_id');
      return;
    }

    this.initializeForm();
    this.loadWarehouse();
    this.loadOrderItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showErrorMessage(messageKey: string, params?: any, life: number = 5000): void {
    let errorMessage = this.translateService.instant(messageKey, params);
    
    // If translation key not found, show a default message
    if (errorMessage === messageKey && params) {
      errorMessage = `Quantity ${params.quantity} exceeds plan ${params.plannedQty} for ${params.skuName}`;
    }
    
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: errorMessage,
      life,
    });
  }


  private refreshPickLinesUI(): void {
    this.updatePickLineGroups();
    this.updateODOListDataSource(); // Update ODO list when pick lines change
    
    // Re-validate quantity field if product is selected (to update max value)
    const selectedProduct = this.addTrackingLineForm.get('product')?.value;
    if (selectedProduct) {
      this.updateFieldValidators(selectedProduct);
    }
    
    this.cdr.markForCheck();
  }

  private refreshTableData(): void {
    this.flattenTableData();
    this.cdr.markForCheck();
  }

  private updatePickLineGroups(): void {
    const groupMap = new Map<string, PickLineGroup>();
    
    this.pickLines.forEach(line => {
      const key = line.skuCode;
      if (!groupMap.has(key)) {
        const shouldExpand = this.expandedSkuGroups.has(key) || 
          (this.pickLines.filter(l => l.skuCode === key).length > 1);
        if (shouldExpand && !this.expandedSkuGroups.has(key)) {
          this.expandedSkuGroups.add(key);
        }
        
        const item = this.items.find(i => i.skuCode === key);
        groupMap.set(key, {
          skuCode: line.skuCode,
          skuName: line.skuName,
          spuCode: line.spuCode,
          lines: [],
          totalQuantity: 0,
          isExpanded: this.expandedSkuGroups.has(key),
          plannedQty: item?.plannedQty,
          pickedQty: item?.pickedQty,
          waitOutQty: item?.waitOutQty,
        });
      }
      
      const group = groupMap.get(key)!;
      group.lines.push(line);
      group.totalQuantity += line.quantity;
    });
    
    this.pickLineGroups = Array.from(groupMap.values());
    this.flattenTableData();
  }

  private flattenTableData(): void {
    const flatData: any[] = [];
    
    this.pickLineGroups.forEach(group => {
      // Combine SKU code and name for display
      const skuDisplayName = group.skuCode && group.skuName 
        ? `${group.skuCode} - ${group.skuName}`
        : group.skuName || group.skuCode || '';
      
      // If only one line, show as single row (no group header)
      if (group.lines.length === 1) {
        const line = group.lines[0];
        const lineSkuDisplayName = line.skuCode && line.skuName
          ? `${line.skuCode} - ${line.skuName}`
          : line.skuName || line.skuCode || '';
        flatData.push({
          ...line,
          skuName: lineSkuDisplayName,
          isGroupHeader: false,
          isSubRow: false,
          isEditing: this.editingTrackingLineId === line.id,
        });
      } else {
        // Add group header
        flatData.push({
          skuCode: group.skuCode,
          skuName: skuDisplayName,
          quantity: group.totalQuantity,
          plannedQty: group.plannedQty,
          pickedQty: group.pickedQty,
          waitOutQty: group.waitOutQty,
          isGroupHeader: true,
          isExpanded: group.isExpanded,
        });
        
        // Add sub-rows if expanded
        if (group.isExpanded) {
          group.lines.forEach(line => {
            const lineSkuDisplayName = line.skuCode && line.skuName
              ? `${line.skuCode} - ${line.skuName}`
              : line.skuName || line.skuCode || '';
            flatData.push({
              ...line,
              skuName: lineSkuDisplayName,
              isGroupHeader: false,
              isSubRow: true,
              isEditing: this.editingTrackingLineId === line.id,
            });
          });
        }
      }
    });
    
    this.tableDataSource = flatData;
  }

  resetTrackingLineForm(): void {
    this.addTrackingLineForm.reset({
      product: null,
      lot: '',
      serial: [],
      quantity: 0,
      barcode: '',
    });
    this.selectedProduct = null;
    this.previousProductSkuCode = null;
    this.editingTrackingLineId = null;
  }

  private initializeForm(): void {
    this.trackingForm = this.fb.group({
      trackingLines: this.fb.array([]),
    });
    this.trackingLinesFormArray = this.trackingForm.get('trackingLines') as FormArray;

    this.addTrackingLineForm = this.fb.group({
      product: [null],
      lot: [{ value: '', disabled: true }],
      serial: [{ value: [] }],
      quantity: [0, [Validators.required, Validators.min(0)]],
      barcode: [''],
    });

    // Watch for product changes
    this.addTrackingLineForm.get('product')?.valueChanges.subscribe((product: OutboundDeliveryItem | null) => {
      const currentSkuCode = product?.skuCode || null;
      const productChanged = this.previousProductSkuCode !== currentSkuCode;
      
      this.selectedProduct = product;
      
      if (productChanged) {
        // Calculate remaining quantity for selected product
        const remainingQty = this.getRemainingQuantityForSku(product?.skuCode || '');
        
        this.addTrackingLineForm.patchValue({ 
          lot: '',
          serial: [],
          quantity: remainingQty > 0 ? remainingQty : 1,
        }, { emitEvent: false });
        
        this.addTrackingLineForm.get('lot')?.markAsUntouched();
        this.addTrackingLineForm.get('serial')?.markAsUntouched();
        this.addTrackingLineForm.get('quantity')?.markAsUntouched();
      }
      
      this.previousProductSkuCode = currentSkuCode;
      this.updateFieldValidators(product);
      
      // Re-validate quantity when product changes
      this.addTrackingLineForm.get('quantity')?.updateValueAndValidity();
      
      this.cdr.markForCheck();
    });
  }

  private loadOrderItems(): void {
    if (!this.orderId) return;

    this.loading = true;
    
    this.stockDeliveryService
      .getDeliveryOrderDetails(this.orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (outboundItems: OutboundDeliveryItem[]) => {
          this.items = outboundItems;
          this.updateODOListDataSource();
          this.loadSpuPolicies();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading outbound items:', error);
          this.showErrorMessage('inbound.receive.failed_to_load_items');
          this.items = [];
          this.updateODOListDataSource();
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private updateODOListDataSource(): void {
    // Calculate total picked quantity in current session for each SKU
    const pickedInSession = new Map<string, number>();
    this.pickLines.forEach(line => {
      const currentQty = pickedInSession.get(line.skuCode) || 0;
      pickedInSession.set(line.skuCode, currentQty + (line.quantity || 0));
    });

    // Map items and calculate remaining quantity
    const allItems = this.items.map(item => {
      const plannedQty = item.plannedQty || 0;
      const pickedQty = item.pickedQty || 0; // Already picked from previous sessions
      const pickedInThisSession = pickedInSession.get(item.skuCode || '') || 0;
      const remainingQty = plannedQty - pickedQty - pickedInThisSession;

      return {
        skuCode: item.skuCode,
        skuName: item.skuName || item.skuCode,
        plannedQty: plannedQty,
        pickedQty: pickedQty,
        remainingQty: Math.max(0, remainingQty), // Ensure non-negative
        originalItem: item, // Keep reference to original item for auto-fill
      };
    });

    // Filter to only show items with remainingQty > 0
    this.odoListDataSource = allItems.filter(item => item.remainingQty > 0);
    
    this.cdr.markForCheck();
  }

  /**
   * Handle ODO item selection and auto-fill form
   */
  onODOItemSelect(selectedItem: any): void {
    this.selectedODOItem = selectedItem;
    
    // Find the original item from items array
    const originalItem = this.items.find(item => item.skuCode === selectedItem.skuCode);
    if (!originalItem) {
      return;
    }

    // Auto-fill form with selected item
    this.addTrackingLineForm.patchValue({
      product: originalItem,
      quantity: selectedItem.remainingQty > 0 ? selectedItem.remainingQty : 1,
    }, { emitEvent: false });

    // Update selected product
    this.selectedProduct = originalItem;
    this.previousProductSkuCode = originalItem.skuCode || null;
    
    // Update field validators
    this.updateFieldValidators(originalItem);
    this.applyDefaultLot(originalItem, selectedItem.remainingQty);
    
    this.cdr.markForCheck();
  }

  private loadWarehouse(): void {
    const id = Number(this.orderId);
    if (!Number.isFinite(id)) {
      return;
    }
    this.outboundDeliveryOrderService
      .detail7(id, 'body', false, { httpHeaderAccept: 'application/json' as any })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) =>
          BlobConverterUtil.isBlob(response) ? from(BlobConverterUtil.convertToJson(response)) : of(response)
        )
      )
      .subscribe((info: any) => {
        this.warehouseId = info?.outLogicWarehouseId ?? null;
        this.refreshEligibleOptions();
      });
  }

  private refreshEligibleOptions(): void {
    if (!this.warehouseId || !this.items?.length) {
      return;
    }
    const skuCodes = [...new Set(this.items.map(item => item.skuCode).filter((code): code is string => Boolean(code)))];
    if (skuCodes.length === 0) {
      return;
    }
    forkJoin(
      skuCodes.map(skuCode =>
        this.batchManagementService.listEligibleLots(skuCode, this.warehouseId as number).pipe(
          switchMap((response: any) =>
            BlobConverterUtil.isBlob(response) ? from(BlobConverterUtil.convertToJson(response)) : of(response)
          ),
          catchError(() => of([]))
        )
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups: any[]) => {
        this.lotOptionsMap = this.groupBatchesBySku(groups.flat());
        this.cdr.markForCheck();
      });
  }

  private applyDefaultLot(item: OutboundDeliveryItem, quantity: number): void {
    if (item.batchId) {
      this.addTrackingLineForm.patchValue({ lot: item.batchId }, { emitEvent: false });
      return;
    }
    if (!item.policy?.requireLot || !item.skuCode || !this.warehouseId) {
      return;
    }
    this.batchManagementService
      .resolveSale({
        skuCode: item.skuCode,
        warehouseId: this.warehouseId,
        quantity: quantity > 0 ? quantity : 1,
        suggestOnly: true,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) =>
          BlobConverterUtil.isBlob(response) ? from(BlobConverterUtil.convertToJson(response)) : of(response)
        )
      )
      .subscribe((result: any) => {
        const suggested = result?.allocations?.[0]?.batchId ?? result?.fefoSuggested?.[0]?.batchId;
        if (suggested) {
          (item as OutboundDeliveryItem & { fefoSuggestedBatchId?: string }).fefoSuggestedBatchId =
            suggested;
          this.addTrackingLineForm.patchValue({ lot: suggested }, { emitEvent: false });
          this.cdr.markForCheck();
        }
      });
  }

  private loadSpuPolicies(): void {
    if (!this.items || this.items.length === 0) {
      this.loading = false;
      return;
    }

    const skuCodes = this.items.map(item => item.skuCode).filter((code): code is string => Boolean(code));
    if (skuCodes.length === 0) {
      this.loading = false;
      return;
    }

    // Load serial profiles by SKU codes
    this.serialProfileManagementService
      .getSerialProfileMapBySkuCodes(skuCodes, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        catchError((error) => {
          console.error('Error loading serial profiles:', error);
          return of({});
        })
      )
      .subscribe({
        next: (profileMap: { [key: string]: any }) => {
          this.items.forEach(item => {
            const profile = profileMap[item.skuCode || ''];
            if (profile) {
              item.policy = {
                requireLot: profile.lotRequired || false,
                requireExpiry: profile.expiryRequired || false,
                requireSerial: profile.serialRequired || false,
              };
            }
          });
          if (this.selectedProduct?.skuCode) {
            const refreshed = this.items.find(item => item.skuCode === this.selectedProduct?.skuCode);
            if (refreshed) {
              this.selectedProduct = refreshed;
              this.updateFieldValidators(refreshed);
              const qty = Number(this.addTrackingLineForm.get('quantity')?.value) || 1;
              this.applyDefaultLot(refreshed, qty);
            }
          }

          this.cdr.markForCheck();
          this.loadAllBatchInfo();
        },
        error: (error) => {
          console.error('Error loading SPU policies:', error);
          this.loading = false;
        },
      });
  }

  private loadAllBatchInfo(): void {
    if (!this.items || this.items.length === 0) {
      this.loading = false;
      return;
    }

    const skuCodes = this.items.map(item => item.skuCode).filter((code): code is string => Boolean(code));
    if (skuCodes.length === 0) {
      this.loading = false;
      return;
    }

    // Load batches by SKU codes
    this.batchManagementService
      .getBatchListBySkuCodes(skuCodes, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        map((data: any) => Array.isArray(data) ? data : []),
        map((batches: any[]) => this.groupBatchesBySku(batches)),
        catchError((error) => {
          console.error('Error loading batch information:', error);
          return of(new Map<string, string[]>());
        })
      )
      .subscribe({
        next: (lotMap: Map<string, string[]>) => {
          this.lotOptionsMap = lotMap;
          this.loading = false;
          this.refreshEligibleOptions();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading batch information:', error);
          this.loading = false;
        },
      });
  }

  private groupBatchesBySku(batches: any[]): Map<string, string[]> {
    const lotMap = new Map<string, string[]>();
    this.lotOptionMap = new Map();

    batches.forEach(batch => {
      const skuCode = batch.skuCode;
      if (!skuCode || !batch.batchId) return;

      const label = batch.lotValue || batch.batchId;
      const options = this.lotOptionMap.get(skuCode) || [];
      if (!options.some(option => option.value === batch.batchId)) {
        options.push({ label, value: batch.batchId });
      }
      this.lotOptionMap.set(skuCode, options);

      if (!lotMap.has(skuCode)) {
        lotMap.set(skuCode, []);
      }
      const lots = lotMap.get(skuCode)!;
      if (!lots.includes(batch.batchId)) {
        lots.push(batch.batchId);
      }
    });

    return lotMap;
  }

  private loadLotNumbers(skuCode: string): void {
    this.lotOptions = (this.lotOptionMap.get(skuCode) || []).map(option => option.value);
  }

  lotChoices(): LotOption[] {
    if (!this.selectedProduct?.skuCode) {
      return [];
    }
    return this.lotOptionMap.get(this.selectedProduct.skuCode) || [];
  }

  private lotLabel(skuCode: string, batchId?: string): string | undefined {
    if (!batchId) {
      return undefined;
    }
    return this.lotOptionMap.get(skuCode)?.find(option => option.value === batchId)?.label || batchId;
  }

  getLotOptionsWithAdd(): LotOption[] {
    const options: LotOption[] = this.lotOptions.map(lot => ({ label: lot, value: lot }));
    options.push({
      label: this.translateService.instant('inbound.receive.add_new_lot'),
      value: '__ADD_NEW__',
      isAddOption: true,
    });
    return options;
  }

  /**
   * Get remaining quantity for a specific SKU
   */
  private getRemainingQuantityForSku(skuCode: string): number {
    if (!skuCode) return 0;
    
    const item = this.items.find(i => i.skuCode === skuCode);
    if (!item) return 0;

    const plannedQty = item.plannedQty || 0;
    const pickedQty = item.pickedQty || 0; // Already picked from previous sessions
    
    // Calculate picked in current session (excluding the line being edited if any)
    const pickedInSession = this.pickLines
      .filter(line => line.skuCode === skuCode && line.id !== this.editingTrackingLineId)
      .reduce((sum, line) => sum + (line.quantity || 0), 0);
    
    const remainingQty = plannedQty - pickedQty - pickedInSession;
    return Math.max(0, remainingQty);
  }

  private updateFieldValidators(product: OutboundDeliveryItem | null): void {
    const lotControl = this.addTrackingLineForm.get('lot');
    const serialControl = this.addTrackingLineForm.get('serial');
    const quantityControl = this.addTrackingLineForm.get('quantity');

    serialControl?.clearValidators();
    serialControl?.updateValueAndValidity({ emitEvent: false });
    if (product?.policy?.requireLot) {
      lotControl?.setValidators([Validators.required]);
    } else {
      lotControl?.clearValidators();
    }
    lotControl?.updateValueAndValidity({ emitEvent: false });

    // Validate quantity with max limit based on remaining quantity
    const skuCode = product?.skuCode || '';
    const maxQuantity = this.getRemainingQuantityForSku(skuCode);
    
    quantityControl?.setValidators([
      Validators.required,
      Validators.min(1),
      this.maxQuantityValidator(maxQuantity)
    ]);
    
    // Update validity but don't emit event to avoid infinite loop
    quantityControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Custom validator for maximum quantity
   */
  private maxQuantityValidator(maxValue: number) {
    return (control: any) => {
      if (!control.value || maxValue <= 0) {
        return null;
      }
      const value = Number(control.value);
      if (isNaN(value) || value <= 0) {
        return null; // Let min validator handle this
      }
      if (value > maxValue) {
        return { maxQuantity: { max: maxValue, actual: value } };
      }
      return null;
    };
  }

  isFormFieldRequired(field: string): boolean {
    const control = this.addTrackingLineForm.get(field);
    return control?.hasError('required') || false;
  }

  getProductOptions(): OutboundDeliveryItem[] {
    if (!this.items || this.items.length === 0) {
      return [];
    }

    // Filter to only show items with remaining quantity > 0
    return this.items.filter(item => {
      const remainingQty = this.getRemainingQuantityForSku(item.skuCode || '');
      return remainingQty > 0;
    });
  }

  getSelectedProductPlan(): number {
    return this.selectedProduct?.plannedQty || 0;
  }

  getSelectedProductAlreadyOutQty(): number {
    const pickedQty = this.selectedProduct?.pickedQty || 0;
    return pickedQty;
  }

  /**
   * Validate if quantity can be picked: quantity <= plannedQty - pickedQty
   * @param skuCode SKU code to validate
   * @param newQuantity New quantity to add/update
   * @param excludeLineId Optional: ID of pick line to exclude from current total (for update case)
   * @returns Validation result with isValid, currentTotal, plannedQty, pickedQty
   */
  private validateQuantityNotExceedingPlan(
    skuCode: string,
    newQuantity: number,
    excludeLineId?: string
  ): { isValid: boolean; currentTotal: number; plannedQty: number; pickedQty: number } {
    // Find the product item
    const product = this.items.find(item => item.skuCode === skuCode);
    if (!product) {
      return { isValid: true, currentTotal: 0, plannedQty: 0, pickedQty: 0 };
    }

    const plannedQty = product.plannedQty || 0;
    // Get picked quantity from previous sessions (đã lấy trước đó)
    const pickedQty = product.pickedQty || 0;

    // Calculate current total from pick lines in this session (excluding the line being edited if provided)
    const currentTotal = this.pickLines
      .filter(line => line.skuCode === skuCode && line.id !== excludeLineId)
      .reduce((sum, line) => sum + (line.quantity || 0), 0);

    // Số lượng còn lại có thể lấy = plannedQty - pickedQty
    // Số lượng mới (sau khi trừ đi currentTotal nếu đang edit) phải <= số lượng còn lại
    // Tức là: (currentTotal + newQuantity) <= (plannedQty - pickedQty)
    // Hoặc: newQuantity <= (plannedQty - pickedQty - currentTotal)
    const remainingQty = plannedQty - pickedQty;
    const totalNewQty = currentTotal + newQuantity;
    
    return {
      isValid: totalNewQty <= remainingQty,
      currentTotal: currentTotal,
      plannedQty: plannedQty,
      pickedQty: pickedQty,
    };
  }

  /**
   * Show error message when quantity exceeds planned quantity
   * @param validation - Validation result from validateQuantityNotExceedingPlan
   * @param quantity - New quantity being added
   * @param skuCode - SKU code
   * @param skuName - SKU name (optional)
   */
  private showQuantityExceedsPlanError(
    validation: { isValid: boolean; currentTotal: number; plannedQty: number; pickedQty: number },
    quantity: number,
    skuCode: string,
    skuName?: string
  ): void {
    // Số lượng còn lại có thể lấy = plannedQty - pickedQty
    const remainingQty = validation.plannedQty - validation.pickedQty;
    const totalNewQty = validation.currentTotal + quantity;
    
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant('outbound_management.messages.quantity_exceeds_plan', {
        current: totalNewQty,
        remaining: remainingQty,
        planned: validation.plannedQty,
        picked: validation.pickedQty,
        sku: skuName || skuCode,
      }),
      life: 5000,
    });
  }

  onCancel(): void {
    this.router.navigate(['/stock/delivery/delivery-order']);
  }

  onConfirm(): void {
    if (!this.orderId || this.pickLines.length === 0) {
      this.showErrorMessage('inbound.receive.at_least_one_tracking_line_required');
      return;
    }

    const orderIdNum = parseInt(this.orderId, 10);
    if (isNaN(orderIdNum)) {
      this.showErrorMessage('inbound.receive.invalid_warrant_id');
      return;
    }

    // Transform pick lines to API payload format
    const missingLot = this.pickLines.find(line => line.policy?.requireLot && !line.batchId);
    if (missingLot) {
      this.showErrorMessage('inbound.receive.select_lot');
      return;
    }

    this.pendingFefoLines = this.pickLines.filter(
      (line) =>
        line.policy?.requireLot &&
        line.fefoSuggestedBatchId &&
        line.batchId &&
        line.batchId !== line.fefoSuggestedBatchId &&
        !line.overrideReason?.trim(),
    );
    if (this.pendingFefoLines.length > 0) {
      this.openNextFefoOverrideDialog();
      return;
    }

    this.submitPicking(orderIdNum);
  }

  onFefoOverrideResult(result: FefoOverrideDialogResult): void {
    const current = this.pendingFefoLines[0];
    if (!result.confirmed || !result.reason?.trim()) {
      this.pendingFefoLines = [];
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'outbound_management.messages.fefo_override_required',
        ),
      });
      return;
    }
    if (current) {
      current.overrideReason = result.reason.trim();
    }
    this.pendingFefoLines = this.pendingFefoLines.slice(1);
    if (this.pendingFefoLines.length > 0) {
      this.openNextFefoOverrideDialog();
      return;
    }
    const orderIdNum = parseInt(this.orderId || '', 10);
    if (!isNaN(orderIdNum)) {
      this.submitPicking(orderIdNum);
    }
  }

  private openNextFefoOverrideDialog(): void {
    const line = this.pendingFefoLines[0];
    if (!line) {
      return;
    }
    this.fefoOverrideData = {
      skuCode: line.skuCode,
      chosenLot: line.batchId,
      suggestedLot: line.fefoSuggestedBatchId,
    };
    this.fefoOverrideVisible = true;
    this.cdr.markForCheck();
  }

  private submitPicking(orderIdNum: number): void {
    const items: PickingItemDTO[] = this.pickLines.map(line => ({
      skuCode: line.skuCode,
      actualQty: line.quantity,
      batchId: line.batchId,
      overrideReason: line.overrideReason,
    }));

    const payload: OutboundDeliveryOrderPickingDTO = {
      outboundDeliveryOrderId: orderIdNum,
      items: items,
    };

    this.loading = true;

    this.outboundDeliveryOrderService
      .confirmPicking(payload, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        })
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.skipCanDeactivate = true;
          this.trackingForm?.markAsPristine();
          this.addTrackingLineForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('outbound_management.messages.picked_successfully'),
          });
          setTimeout(() => {
            this.router.navigate(['/stock/delivery/delivery-order']);
          }, 1000);
        },
        error: (error: any) => {
          this.loading = false;
          const errorMessage = error?.error?.message || error?.message || 'Failed to confirm picking';
          this.errorDialogService.showError(this.errorTranslatorService.translate(errorMessage));
        },
      });
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading) {
      return true;
    }

    return !(
      this.trackingForm?.dirty ||
      this.addTrackingLineForm?.dirty ||
      this.pickLines.length > 0
    );
  }

  onAddTrackingLine(): void {
    // Mark all fields as touched to show validation errors
    this.addTrackingLineForm.markAllAsTouched();

    // Validate form
    if (!this.addTrackingLineForm.valid) {
      return;
    }

    const formValue = this.addTrackingLineForm.value;
    const selectedItem = formValue.product as OutboundDeliveryItem | null;

    // Product is REQUIRED
    if (!selectedItem) {
      this.addTrackingLineForm.get('product')?.setErrors({ required: true });
      this.addTrackingLineForm.get('product')?.markAsTouched();
      this.showErrorMessage('inbound.receive.product_required');
      return;
    }

    // Prepare pick line data (no lot/serial/expiry for outbound)
    const quantity = formValue.quantity || 1;
    const skuCode = selectedItem.skuCode || '';

    // Check if we're editing an existing line
    if (this.editingTrackingLineId) {
      const existingLine = this.pickLines.find(line => line.id === this.editingTrackingLineId);
      if (existingLine) {
        // Validate quantity before update
        const validation = this.validateQuantityNotExceedingPlan(
          skuCode,
          quantity,
          this.editingTrackingLineId
        );

        if (!validation.isValid) {
          this.showQuantityExceedsPlanError(
            validation,
            quantity,
            skuCode,
            selectedItem.skuName
          );
          return;
        }
        
        existingLine.skuCode = skuCode;
        existingLine.skuName = selectedItem.skuName || '';
        existingLine.spuCode = selectedItem.spuCode || '';
        existingLine.quantity = quantity;
        existingLine.plannedQty = selectedItem.plannedQty;
        existingLine.pickedQty = selectedItem.pickedQty;
        existingLine.waitOutQty = selectedItem.waitOutQty;
        existingLine.policy = selectedItem.policy;
        existingLine.batchId = selectedItem.policy?.requireLot ? formValue.lot : undefined;
        existingLine.lot = this.lotLabel(skuCode, existingLine.batchId);
        existingLine.fefoSuggestedBatchId = (
          selectedItem as OutboundDeliveryItem & { fefoSuggestedBatchId?: string }
        ).fefoSuggestedBatchId;

        this.refreshPickLinesUI();
        this.editingTrackingLineId = null;
        this.refreshTableData();
      }
    } else {
      // Check if line with same SKU exists (for outbound, we merge by SKU only)
      const batchId = selectedItem.policy?.requireLot ? formValue.lot : undefined;
      const existingLine = this.pickLines.find(
        line => line.skuCode === skuCode && (!selectedItem.policy?.requireLot || line.batchId === batchId)
      );

      if (existingLine) {
        // Validate quantity before increasing
        const validation = this.validateQuantityNotExceedingPlan(
          skuCode,
          quantity,
          undefined // Don't exclude any line since we're adding to existing
        );

        if (!validation.isValid) {
          this.showQuantityExceedsPlanError(
            validation,
            quantity,
            skuCode,
            selectedItem.skuName
          );
          return;
        }
        
        // Merge quantities
        existingLine.quantity = (existingLine.quantity || 0) + quantity;
        this.refreshPickLinesUI();
      } else {
        // Validate quantity before creating new line
        const validation = this.validateQuantityNotExceedingPlan(
          skuCode,
          quantity,
          undefined
        );

        if (!validation.isValid) {
          this.showQuantityExceedsPlanError(
            validation,
            quantity,
            skuCode,
            selectedItem.skuName
          );
          return;
        }
        // Create new pick line
        const batchId = selectedItem.policy?.requireLot ? formValue.lot : undefined;
        const newPickLine: PickLine = {
          id: Date.now().toString(),
          skuCode: skuCode,
          skuName: selectedItem.skuName || '',
          spuCode: selectedItem.spuCode || '',
          quantity: quantity,
          lot: this.lotLabel(skuCode, batchId),
          batchId,
          fefoSuggestedBatchId: (
            selectedItem as OutboundDeliveryItem & { fefoSuggestedBatchId?: string }
          ).fefoSuggestedBatchId,
          plannedQty: selectedItem.plannedQty,
          pickedQty: selectedItem.pickedQty,
          waitOutQty: selectedItem.waitOutQty,
          policy: selectedItem.policy,
        };

        this.pickLines.push(newPickLine);
        this.refreshPickLinesUI();
      }
    }

    // Reset form
    this.resetTrackingLineForm();
    if (this.editingTrackingLineId) {
      this.editingTrackingLineId = null;
      this.refreshTableData();
    }
  }

  onFormLotChange(event: any): void {
    // TODO: Handle lot change
  }

  onFormBarcodeInput(): void {
    const formBarcode = this.addTrackingLineForm.get('barcode')?.value;
    const barcodeValue = (formBarcode || this.formBarcodeInput || '')
      .toString()
      .trim();
    if (!barcodeValue) {
      this.showErrorMessage('inbound.receive.barcode_empty');
      return;
    }
    this.lookupProductByBarcode(barcodeValue);
  }

  /**
   * Resolve SKU barcode and/or lot barcode (GS1 / custom). When the SKU requires
   * lot tracking, auto-select the matching lot option from the scan payload.
   */
  private lookupProductByBarcode(barcode: string): void {
    this.scanningBarcode = true;
    this.addTrackingLineForm.get('barcode')?.disable({ emitEvent: false });
    const trimmedBarcode = barcode.trim();
    const clientParsed = parseScannedBarcode(trimmedBarcode);

    this.barcodeResolverService
      .getSkuByBarcode(trimmedBarcode)
      .pipe(
        switchMap((response: unknown) =>
          from(BlobConverterUtil.convertToJson(response)),
        ),
        catchError(() => of(null)),
        switchMap((skuDetail: BarcodeResolveResultVO | null) => {
          if (skuDetail?.gs1ParseResult) {
            return of({
              skuDetail,
              parseResult: skuDetail.gs1ParseResult as Gs1ParseResultVO,
            });
          }
          if (!skuDetail) {
            return of({
              skuDetail,
              parseResult: null as Gs1ParseResultVO | null,
            });
          }
          return this.barcodeResolverService.getGs1ParseResult(trimmedBarcode).pipe(
            switchMap((response: unknown) =>
              from(BlobConverterUtil.convertToJson(response)),
            ),
            catchError(() => of(null)),
            map((parseResult) => ({
              skuDetail,
              parseResult: parseResult as Gs1ParseResultVO | null,
            })),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          this.scanningBarcode = false;
          this.addTrackingLineForm.get('barcode')?.enable({ emitEvent: false });

          const skuDetail = result.skuDetail as BarcodeResolveResultVO | null;
          const apiParse = result.parseResult as Gs1ParseResultVO | null;
          const lotFromScan =
            apiParse?.lot || clientParsed.batch || '';

          if (!skuDetail?.skuCode) {
            // Lot-only scan while a product is already selected: fill lot field.
            if (this.selectedProduct?.policy?.requireLot && lotFromScan) {
              this.applyLotFromScan(this.selectedProduct, lotFromScan);
              this.addTrackingLineForm.patchValue({ barcode: '' });
              this.formBarcodeInput = '';
              return;
            }
            this.showErrorMessage('inbound.receive.error_finding_sku');
            return;
          }

          const matchingItem = this.items.find(
            (item) => item.skuCode === skuDetail.skuCode,
          );
          if (!matchingItem) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translateService.instant('common.messages.warning'),
              detail: this.translateService.instant(
                'inbound.receive.correct_barcode_prompt_not_in_warrant',
                { barcode: trimmedBarcode, sku: skuDetail.skuCode },
              ),
              life: 4000,
            });
            return;
          }

          this.selectedProduct = matchingItem;
          this.previousProductSkuCode = matchingItem.skuCode || null;
          const remaining = this.getRemainingQuantityForSku(matchingItem.skuCode || '');
          this.addTrackingLineForm.patchValue({
            product: matchingItem,
            barcode: '',
            quantity: Math.max(1, remaining),
          });
          this.formBarcodeInput = '';
          this.updateFieldValidators(matchingItem);

          if (matchingItem.skuCode) {
            this.loadLotNumbers(matchingItem.skuCode);
          }

          // Outbound picks existing warehouse lots — match scanned lot to eligible options.
          if (matchingItem.policy?.requireLot && lotFromScan) {
            this.applyLotFromScan(matchingItem, lotFromScan);
          }

          this.lotFromBarcode = lotFromScan || undefined;
        },
        error: () => {
          this.scanningBarcode = false;
          this.addTrackingLineForm.get('barcode')?.enable({ emitEvent: false });
          this.showErrorMessage('inbound.receive.error_finding_sku');
        },
      });
  }

  private applyLotFromScan(item: OutboundDeliveryItem, lotValue: string): void {
    if (!item.skuCode || !lotValue) {
      return;
    }
    const options = this.lotOptionMap.get(item.skuCode) || [];
    const match =
      options.find(
        (option) =>
          option.value === lotValue ||
          option.label === lotValue ||
          option.label?.startsWith(`${lotValue} `),
      ) ||
      options.find((option) =>
        (option.label || '').toUpperCase().includes(lotValue.toUpperCase()),
      );

    if (match) {
      this.addTrackingLineForm.patchValue({ lot: match.value });
      this.addTrackingLineForm.get('lot')?.enable({ emitEvent: false });
      return;
    }

    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.messages.warning'),
      detail: this.translateService.instant('inbound.receive.lot_not_in_options', {
        lot: lotValue,
        sku: item.skuCode,
      }),
      life: 4000,
    });
  }

  toggleSkuGroup(skuCode: string): void {
    if (this.expandedSkuGroups.has(skuCode)) {
      this.expandedSkuGroups.delete(skuCode);
    } else {
      this.expandedSkuGroups.add(skuCode);
    }
    this.updatePickLineGroups();
    this.refreshTableData();
  }

  removeSkuGroup(skuCode: string): void {
    // Remove all lines with this SKU code
    this.pickLines = this.pickLines.filter(line => line.skuCode !== skuCode);
    this.expandedSkuGroups.delete(skuCode);
    this.refreshPickLinesUI();
  }

  /**
   * Pick all items from ODO
   */
  onPickAllODO(): void {
    if (this.items.length === 0) {
      return;
    }

    // Calculate current remaining quantities (considering already picked in session)
    const pickedInSession = new Map<string, number>();
    this.pickLines.forEach(line => {
      const currentQty = pickedInSession.get(line.skuCode) || 0;
      pickedInSession.set(line.skuCode, currentQty + (line.quantity || 0));
    });

    // Add all items with remaining quantity > 0
    this.items.forEach(item => {
      const plannedQty = item.plannedQty || 0;
      const pickedQty = item.pickedQty || 0; // Already picked from previous sessions
      const pickedInThisSession = pickedInSession.get(item.skuCode || '') || 0;
      const remainingQty = plannedQty - pickedQty - pickedInThisSession;

      if (remainingQty > 0) {
        // Check if line already exists
        const existingLine = this.pickLines.find(line => line.skuCode === item.skuCode);
        if (existingLine) {
          // Update quantity to remaining
          existingLine.quantity = remainingQty;
        } else {
          // Create new pick line
          const newPickLine: PickLine = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            skuCode: item.skuCode || '',
            skuName: item.skuName || '',
            spuCode: item.spuCode || '',
            quantity: remainingQty,
            plannedQty: item.plannedQty,
            pickedQty: item.pickedQty,
            waitOutQty: item.waitOutQty,
            policy: item.policy,
          };
          this.pickLines.push(newPickLine);
        }
      }
    });

    this.refreshPickLinesUI();
  }

  /**
   * Edit pick line - populate form with pick line data
   */
  onEditTrackingLine(rowData: any): void {
    // Find the pick line
    const pickLine = this.pickLines.find(line => line.id === rowData.id);
    if (!pickLine) {
      return;
    }
    const product = this.items.find(item => item.skuCode === pickLine.skuCode);
    if (!product) {
      this.showErrorMessage('inbound.receive.product_not_found');
      return;
    }
    
    this.editingTrackingLineId = pickLine.id;
    
    this.addTrackingLineForm.patchValue({
      product: product,
      lot: '',
      serial: [],
      quantity: pickLine.quantity || 1,
    }, { emitEvent: false });
    
    this.updateFieldValidators(product);
    this.selectedProduct = product;
    this.previousProductSkuCode = product.skuCode || null;
    this.refreshTableData();
    
    // Scroll to form
    const formElement = document.querySelector('.add-tracking-line-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Cancel edit mode - reset form and clear editing state
   */
  onCancelEdit(): void {
    this.editingTrackingLineId = null;
    this.resetTrackingLineForm();
    this.refreshTableData();
  }

  /**
   * Remove a single pick line
   */
  removeTrackingLine(line: PickLine): void {
    this.pickLines = this.pickLines.filter((l) => l.id !== line.id);
    this.refreshPickLinesUI();
  }
}

